import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import { OIDLoginWidgetExportData } from '@cudirect-widgets/ea-login';
import { JwtToken, TokenService } from '@cudirect/shared-ui-library';
import { ConsumerLogin } from '@user/model/consumer-login';
import { CustomModal } from '@workflow/dynamic-render/page/shared/core-custom-page.decorator';
import { CorePageBaseComponent } from '@workflow/dynamic-render/page/shared/core-page-base.component';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { OaoOnClickFunctionsService } from '@workflow/ecp-workflow/oao/services/logic-functions/oao-on-click-functions.service';
import { OlaOnClickFunctionsService } from '@workflow/ecp-workflow/ola/services/logic-functions/on-click-functions.service';
import { WorkflowPageNavigationService } from '@workflow/service/workflow-page-navigation/workflow-page-navigation.service';
import { WorkflowWidgetEventServiceService } from '@workflow/widget/workflow-widget-event.service';

/**
 * Silent Verification (modal) Component is only used for REcaptcha which is triggered on personal info page.
 * TODO: This component needs a second look / possible refactor or can be removed.
 */
@CustomModal('silent-verification')
@Component({
  selector: 'ecp-silent-verification',
  templateUrl: 'silent-verification.component.html'
})
export class SilentVerificationComponent extends CorePageBaseComponent implements OnInit {
  public userId: string;
  public verifyMethod: 'Email_Code' | 'Phone';
  private isBusiness: boolean;
  private readonly statefulService = inject(StatefulService);

  constructor(
    workflowPageNavigationService: WorkflowPageNavigationService,
    widgetEventService: WorkflowWidgetEventServiceService,
    private readonly oaoOnlick: OaoOnClickFunctionsService,
    private readonly olaOnlick: OlaOnClickFunctionsService,
    private readonly applicationApiService: ApplicationApiService,
    private readonly tokenService: TokenService,
    private readonly userService: UserService,
    private readonly router: Router
  ) {
    super(workflowPageNavigationService, widgetEventService);
  }

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  private registerPayload?: ConsumerLogin & { isSilentRegistration?: boolean };
  // Can ConsumerLogin type just include the optional property?

  public ngOnInit() {
    if (this.page.flowId.indexOf('ola') === 0) {
      // If we're having to do this kind of coercion, we should probably just fix the type
      this.registerPayload = this.olaOnlick.getRegisterPayload() as unknown as ConsumerLogin;
    } else {
      this.registerPayload = this.oaoOnlick.getRegisterPayload() as unknown as ConsumerLogin;
      const { isBusiness } = this.oaoOnlick.getPrimaryRoot();
      this.isBusiness = isBusiness;
    }
    this.registerPayload.isSilentRegistration = false;
    this.verifyMethod = !!this.registerPayload.cellPhone ? 'Phone' : 'Email_Code';
    if (this.verifyMethod === 'Phone') {
      this.userId = this.registerPayload.cellPhone;
    } else {
      this.userId = this.registerPayload.email;
    }
  }

  public receivedDataHandler(data: OIDLoginWidgetExportData): void {
    const request: ConsumerLogin = this.registerPayload;
    if (typeof data.token !== 'string') {
      request.accessToken = data.token.access_token;
      request.refreshToken = data.token.refresh_token;
      request.loginWithSSO = false;
      request.idToken = '';
      request.discoveryURL = '';
    }
    this.applicationApiService.verifiedRegistration(
      request,
      (response) => {
        this.SetUser(response.data);
        this.tokenService.updateToken(data.token as JwtToken);
        if (this.page.flowId.indexOf('ola') === 0) {
          // this.statefulService.wrap('ola:isRegisteredApplication').value = true;
          this.statefulService.wrap('mem:edit-ola-personal-preview').value = false;
        } else if (this.page.flowId.indexOf('oao') === 0) {
          // this.statefulService.wrap('api:isRegisteredApplication').value = true;
          this.oaoOnlick.setValueToCondense(this.isBusiness);
        } else {
          console.error('Unknown Flow type');
        }
        this.statefulService.wrap('ss:showMFAVerification').value = false;
        // navigate user to next valid page after successful OTP from reCAPTCHA
        this.router.navigate([this.page.nextPage.flowId + '/' + this.page.nextPage.id]);
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (error) => {
        this.statefulService.wrap('mem:edit-ola-personal-preview').value = false;
        this.oaoOnlick.setValueToCondense(this.isBusiness);
      }
    );
  }

  // These JSDocs need actual information, and this method should be camelCase.
  /**
   * SetUser
   */
  public SetUser(user): void {
    const userInfo: UserInfo = new UserInfo();
    userInfo.email = user.email;
    userInfo.cellPhone = user.cellPhone;
    userInfo.isCellPhoneUser = user.isCellPhoneUser;
    userInfo.token = user.AccessToken;
    userInfo.token_time = new Date();
    userInfo.firstName = user.firstName;
    userInfo.lastName = user.lastName;
    userInfo.userName = `${userInfo.firstName} ${userInfo.lastName}`;
    userInfo.confetti = [];
    userInfo.mfaVerifyMethod = this.verifyMethod;
    this.userService.user = userInfo;
    // TODO: Cross Check
    // this.userSession.startTimer();
    if (this.page.flowId.indexOf('ola') === 0) {
      this.statefulService.wrap('ss:auth-ola').value = user.AssessToken;
    } else {
      this.statefulService.wrap('ss:authorization').value = user.AssessToken;
    }
  }
}
